import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Truck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { api, SERVICES } from "../../lib/api";

const HOME_SIZES = ["1 BHK", "2 BHK", "3 BHK", "4+ BHK", "Studio / Room", "Office"];

export default function QuoteEstimator({ compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    from_city: "",
    to_city: "",
    service_type: "House Shifting",
    home_size: "2 BHK",
    distance_km: "",
    floors: "1",
    move_date: "",
    message: "",
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.from_city || !form.to_city) {
      toast.error("Please fill your name, phone, from and to cities.");
      return;
    }
    setLoading(true);
    setEstimate(null);
    try {
      const est = await api.post("/quote/estimate", {
        from_city: form.from_city,
        to_city: form.to_city,
        service_type: form.service_type,
        home_size: form.home_size,
        distance_km: form.distance_km ? parseInt(form.distance_km) : undefined,
        floors: form.floors ? parseInt(form.floors) : undefined,
        move_date: form.move_date,
      });
      setEstimate(est.data);
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        from_city: form.from_city,
        to_city: form.to_city,
        service_type: form.service_type,
        home_size: form.home_size,
        move_date: form.move_date,
        message: form.message,
        source: "quote_estimator",
      });
      toast.success("Estimate ready — our team will call you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="quote-estimator"
      className={`grid gap-8 ${compact ? "" : "lg:grid-cols-[1.2fr_1fr]"}`}
    >
      <form
        onSubmit={submit}
        className="rounded-2xl bg-background border border-border p-6 md:p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-2xl leading-tight">AI Instant Quote</div>
            <div className="text-xs text-muted-foreground">Powered by Claude · 4.7★ trusted service</div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Full name">
            <Input data-testid="quote-name" value={form.name} onChange={update("name")} placeholder="e.g. Priya Sharma" />
          </Field>
          <Field label="Phone">
            <Input data-testid="quote-phone" value={form.phone} onChange={update("phone")} placeholder="+91 …" />
          </Field>
          <Field label="Email (optional)" className="md:col-span-2">
            <Input data-testid="quote-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
          </Field>
          <Field label="Moving from">
            <Input data-testid="quote-from" value={form.from_city} onChange={update("from_city")} placeholder="Hyderabad" />
          </Field>
          <Field label="Moving to">
            <Input data-testid="quote-to" value={form.to_city} onChange={update("to_city")} placeholder="Bengaluru" />
          </Field>
          <Field label="Service">
            <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
              <SelectTrigger data-testid="quote-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.title}</SelectItem>
                ))}
                <SelectItem value="Office Relocation">Office Relocation</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Home size">
            <Select value={form.home_size} onValueChange={(v) => setForm((f) => ({ ...f, home_size: v }))}>
              <SelectTrigger data-testid="quote-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOME_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Distance (km, optional)">
            <Input data-testid="quote-distance" inputMode="numeric" value={form.distance_km} onChange={update("distance_km")} placeholder="e.g. 570" />
          </Field>
          <Field label="Floors">
            <Input data-testid="quote-floors" inputMode="numeric" value={form.floors} onChange={update("floors")} placeholder="1" />
          </Field>
          <Field label="Preferred move date" className="md:col-span-2">
            <Input data-testid="quote-date" type="date" value={form.move_date} onChange={update("move_date")} />
          </Field>
          <Field label="Additional notes" className="md:col-span-2">
            <Textarea data-testid="quote-notes" rows={3} value={form.message} onChange={update("message")} placeholder="Fragile items, lift/no-lift, anything we should know…" />
          </Field>
        </div>

        <Button
          data-testid="quote-submit-btn"
          type="submit"
          disabled={loading}
          className="mt-6 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Estimating…</>) : "Get my instant estimate"}
        </Button>
      </form>

      <div className="rounded-2xl bg-[#141110] text-[#F0EBE1] p-8 flex flex-col justify-center min-h-[420px]" data-testid="quote-result">
        {!estimate && !loading && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">Estimate preview</div>
            <div className="font-display text-3xl leading-tight mb-4">Your quote appears here in seconds.</div>
            <p className="text-white/70 leading-relaxed">
              Our AI reviews route, home size, floors and season to give you a real price band — not a placeholder. A dedicated ops manager confirms the final number after a quick call.
            </p>
            <div className="mt-8 space-y-3 text-sm text-white/60">
              <div>✓ Transparent line-item breakdown</div>
              <div>✓ Insured transit up to ₹5,00,000</div>
              <div>✓ Reschedule up to 48 hours before</div>
            </div>
          </div>
        )}
        {loading && (
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#C25B48]" />
            <div className="text-white/70">Crunching your move details…</div>
          </div>
        )}
        {estimate && !loading && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25B48] mb-3">Your estimate</div>
            <div className="font-display text-5xl leading-none tracking-tight" data-testid="quote-range">
              ₹{Number(estimate.low).toLocaleString("en-IN")}
              <span className="text-white/50 mx-2">–</span>
              ₹{Number(estimate.high).toLocaleString("en-IN")}
            </div>
            <div className="mt-6 space-y-2">
              {(estimate.breakdown || []).map((b, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-white/10 py-2">
                  <span className="text-white/70">{b.label}</span>
                  <span className="font-medium">{b.amount}</span>
                </div>
              ))}
            </div>
            {estimate.notes && (
              <p className="mt-5 text-xs text-white/60 leading-relaxed">{estimate.notes}</p>
            )}
            <a
              href="tel:+919618222500"
              data-testid="quote-call-cta"
              className="mt-6 inline-flex px-5 py-2.5 rounded-full bg-[#C25B48] hover:bg-[#C25B48]/90 text-white text-sm font-medium"
            >
              Confirm on a call
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
